// 👥 Employés E3b — Exceptions personnelles (propriétaire) : accorder / retirer une permission par catégorie.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });
const CATEGORIES = [
  ['pages', 'Pages'], ['actions', 'Actions'], ['employe_tabs', 'Onglets employé'], ['dossier_onglets', 'Onglets dossier'], ['bon_travail_actions', 'Actions bon de travail'],
];
const libelle = (cle) => cle.replace(/_/g, ' ');

export function ExceptionsPersonnelles({ employee, isProprietaire }) {
  const [data, setData] = useState(null);
  const [grants, setGrants] = useState({});
  const [denies, setDenies] = useState({});
  const [recherche, setRecherche] = useState('');
  const [categorie, setCategorie] = useState('pages');
  const [saving, setSaving] = useState(false);

  const charger = useCallback(async () => {
    if (!employee?.id || !isProprietaire) return;
    try {
      const { data: d } = await axios.get(`${API_URL}/api/employees/${employee.id}/permissions-exceptions`, { headers: headers() });
      setData(d); setGrants(d.grants || {}); setDenies(d.denies || {});
    } catch { setData(null); }
  }, [employee?.id, isProprietaire]);
  useEffect(() => { charger(); }, [charger]);

  const etat = (cat, cle) => (denies[cat]?.[cle] ? 'retiree' : grants[cat]?.[cle] ? 'accordee' : 'role');
  const definir = (cat, cle, nouvel) => {
    const g = { ...grants, [cat]: { ...(grants[cat] || {}) } };
    const d = { ...denies, [cat]: { ...(denies[cat] || {}) } };
    delete g[cat][cle]; delete d[cat][cle];
    if (nouvel === 'accordee') g[cat][cle] = true;
    if (nouvel === 'retiree') d[cat][cle] = true;
    setGrants(g); setDenies(d);
  };
  const nettoyer = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v && Object.keys(v).length > 0));
  const nb = useMemo(() => Object.values(nettoyer(grants)).reduce((n, v) => n + Object.keys(v).length, 0) + Object.values(nettoyer(denies)).reduce((n, v) => n + Object.keys(v).length, 0), [grants, denies]);
  // 👥 E4 — indicateur « Modifications non enregistrées » + garde à la sortie
  const sale = useMemo(() => !!data && (JSON.stringify(nettoyer(grants)) !== JSON.stringify(nettoyer(data.grants || {})) || JSON.stringify(nettoyer(denies)) !== JSON.stringify(nettoyer(data.denies || {}))), [grants, denies, data]);
  useEffect(() => {
    if (!sale) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [sale]);

  const enregistrer = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/employees/${employee.id}/permissions-exceptions`, { grants: nettoyer(grants), denies: nettoyer(denies) }, { headers: headers() });
      toast.success('Exceptions personnelles enregistrées');
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  if (!isProprietaire) return null;
  if (!data) return <Card data-testid="exceptions-personnelles"><CardContent className="py-4 text-sm text-slate-500">Aucun compte lié — les exceptions personnelles s'appliquent à un compte.</CardContent></Card>;
  const cles = (data.definitions?.[categorie] || []).filter((c) => c.toLowerCase().includes(recherche.toLowerCase()));

  return (
    <Card data-testid="exceptions-personnelles">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">Exceptions personnelles <Badge variant="outline" data-testid="exceptions-compteur">{nb}</Badge>
          {sale && <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid="exceptions-non-enregistre">Modifications non enregistrées</Badge>}
        </CardTitle>
        <p className="text-xs text-slate-500">Une permission <strong>retirée</strong> l'emporte sur les rôles ; une permission <strong>accordée</strong> s'ajoute aux rôles. Sinon, c'est la valeur des rôles qui s'applique.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setCategorie(k)} data-testid={`exceptions-cat-${k}`}
              className={`px-2.5 py-1 rounded-full text-xs border ${categorie === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>{l}</button>
          ))}
        </div>
        <Input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher une permission…" data-testid="exceptions-recherche" />
        <ul className="divide-y max-h-72 overflow-auto text-sm" data-testid="exceptions-liste">
          {cles.map((cle) => {
            const e = etat(categorie, cle);
            return (
              <li key={cle} className="flex items-center justify-between py-1.5 gap-2" data-testid={`exception-${categorie}-${cle}`}>
                <span className="capitalize">{libelle(cle)}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant={e === 'accordee' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => definir(categorie, cle, e === 'accordee' ? 'role' : 'accordee')} data-testid={`accorder-${categorie}-${cle}`}>Accorder</Button>
                  <Button size="sm" variant={e === 'retiree' ? 'destructive' : 'outline'} className="h-7 text-xs" onClick={() => definir(categorie, cle, e === 'retiree' ? 'role' : 'retiree')} data-testid={`retirer-${categorie}-${cle}`}>Retirer</Button>
                </div>
              </li>
            );
          })}
          {cles.length === 0 && <li className="py-2 text-slate-500">Aucune permission ne correspond.</li>}
        </ul>
        <div className="flex justify-end gap-2">
          {sale && <Button size="sm" variant="outline" onClick={() => { setGrants(data.grants || {}); setDenies(data.denies || {}); }} data-testid="btn-annuler-exceptions">Annuler</Button>}
          <Button size="sm" disabled={saving || !sale} onClick={enregistrer} data-testid="btn-enregistrer-exceptions">Enregistrer les exceptions</Button>
        </div>
      </CardContent>
    </Card>
  );
}
